import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  EXTERNAL_DELIVERY_MODEL_LABELS,
  EXTERNAL_EVENT_OUTCOME_LABELS,
  EXTERNAL_ORDER_STATUS_COLOR,
  EXTERNAL_ORDER_STATUS_LABELS,
  EXTERNAL_PAYMENT_METHOD_LABELS,
  EXTERNAL_PAYMENT_TYPE_LABELS,
  ExternalOrderDetail,
  FOOD_PROVIDER_LABELS,
  IngredientChange
} from '@/types/FoodDelivery.interface';

interface Props {
  orderId: number | null;
  open: boolean;
  onClose: () => void;
}

const OUTCOME_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  APPLIED: 'success',
  OUT_OF_ORDER_SKIPPED: 'warning',
  TERMINAL_GUARD: 'warning'
};

const fmt = (v: string | null) =>
  v ? new Date(v).toLocaleString('tr-TR') : '—';

/** Bilinmeyen sağlayıcı kodu gizlenmez, ham gösterilir. */
const label = (map: Record<string, string>, code: string | null) =>
  code ? (map[code] ?? code) : null;

/** "Soğansız / +Ekstra peynir" gibi kalem altı içerik değişikliği satırı. */
const IngredientLine: React.FC<{
  prefix: string;
  items: IngredientChange[] | null;
  color: 'error.main' | 'success.main';
}> = ({ prefix, items, color }) =>
  items && items.length > 0 ? (
    <Typography variant="caption" display="block" sx={{ color }}>
      {prefix}
      {items.map((i) => i.name).join(', ')}
    </Typography>
  ) : null;

const OrderDetailDialog: React.FC<Props> = ({ orderId, open, onClose }) => {
  const [detail, setDetail] = useState<ExternalOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Fatura bağlantısı (plan Faz 5).
  const [invoiceInput, setInvoiceInput] = useState('');
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    setError(false);
    foodDeliveryService
      .getOrder(orderId)
      .then((d) => {
        setDetail(d);
        setInvoiceInput(d.invoiceLink ?? '');
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    if (!open || !orderId) return;
    setInvoiceError(null);
    reload();
  }, [open, orderId, reload]);

  const handleSendInvoice = async () => {
    if (!orderId) return;
    setInvoiceBusy(true);
    setInvoiceError(null);
    try {
      await foodDeliveryService.sendInvoiceLink(orderId, invoiceInput.trim());
      reload();
    } catch (err) {
      setInvoiceError(
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? 'Fatura bağlantısı gönderilemedi.'
      );
    } finally {
      setInvoiceBusy(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!orderId) return;
    setInvoiceBusy(true);
    setInvoiceError(null);
    try {
      await foodDeliveryService.deleteInvoiceLink(orderId);
      setInvoiceInput('');
      reload();
    } catch (err) {
      setInvoiceError(
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? 'Fatura bağlantısı silinemedi.'
      );
    } finally {
      setInvoiceBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Platform Siparişi Detayı
        {detail && (
          <>
            <Chip
              size="small"
              sx={{ ml: 1 }}
              label={EXTERNAL_ORDER_STATUS_LABELS[detail.status]}
              color={EXTERNAL_ORDER_STATUS_COLOR[detail.status]}
            />
            {detail.isTestOrder && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                color="info"
                variant="outlined"
                label="Test Siparişi"
              />
            )}
            {detail.isPickup && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                color="secondary"
                variant="outlined"
                label="Gel-Al"
              />
            )}
          </>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : error || !detail ? (
          <Alert severity="error">Sipariş detayı getirilemedi.</Alert>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Platform
                </Typography>
                <Typography>
                  {FOOD_PROVIDER_LABELS[detail.providerCode]}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Sipariş No
                </Typography>
                {/* Müşterinin/çağrı merkezinin gördüğü numara. Paket kimliği
                    64 karakterdir; destek görüşmesinde işe yaramaz. */}
                <Typography>
                  {detail.orderNumber ?? detail.externalOrderId}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Tutar
                </Typography>
                <Typography>
                  {detail.totalAmount
                    ? `${detail.totalAmount} ${detail.currency ?? ''}`
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  İç Sipariş
                </Typography>
                <Typography>
                  {detail.internalOrderId ?? 'Henüz dönüştürülmedi'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Müşteri
                </Typography>
                <Typography>{detail.customerName ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Telefon (maskeli)
                </Typography>
                <Typography>{detail.customerPhone ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Son Olay
                </Typography>
                <Typography>{fmt(detail.lastEventAt)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Ödeme
                </Typography>
                {/* Kapıda ödemede tutarı KURYE tahsil eder — operatörün bunu
                    kaçırmaması için uyarı rengiyle gösterilir. */}
                <Typography
                  color={
                    detail.paymentType === 'PAY_WITH_ON_DELIVERY'
                      ? 'warning.main'
                      : undefined
                  }
                  fontWeight={
                    detail.paymentType === 'PAY_WITH_ON_DELIVERY' ? 600 : 400
                  }
                >
                  {label(EXTERNAL_PAYMENT_TYPE_LABELS, detail.paymentType) ??
                    '—'}
                  {detail.paymentMethod
                    ? ` (${
                        label(
                          EXTERNAL_PAYMENT_METHOD_LABELS,
                          detail.paymentMethod
                        ) ?? detail.paymentMethod
                      })`
                    : ''}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Teslimat
                </Typography>
                <Typography>
                  {detail.isPickup
                    ? 'Gel-Al (müşteri şubeden alacak)'
                    : (label(
                        EXTERNAL_DELIVERY_MODEL_LABELS,
                        detail.deliveryModel
                      ) ?? '—')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Adres
                </Typography>
                <Typography>{detail.deliveryAddress ?? '—'}</Typography>
              </Grid>
            </Grid>

            {/* Müşteri notu mutfak doğruluğu için kritiktir → en görünür yer. */}
            {detail.customerNote && (
              <Alert severity="info" icon={false}>
                <Typography variant="caption" color="text.secondary">
                  Müşteri Notu
                </Typography>
                <Typography fontWeight={600}>{detail.customerNote}</Typography>
              </Alert>
            )}

            <Box>
              <Typography variant="h5" gutterBottom>
                Kalemler ({detail.items.length})
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {detail.items.length === 0 ? (
                <Alert severity="info">Kalem snapshot bilgisi yok.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ürün</TableCell>
                      <TableCell align="right">Adet</TableCell>
                      <TableCell align="right">Birim</TableCell>
                      <TableCell>Eşleşme</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>
                          {it.name}
                          {/* Ekleme/çıkarma mutfağa iletilmezse iade nedeni
                              olur (TGO 4014 "Nota dikkat edilmemiş"). */}
                          <IngredientLine
                            prefix="− "
                            items={it.removedIngredients}
                            color="error.main"
                          />
                          <IngredientLine
                            prefix="+ "
                            items={it.addedIngredients}
                            color="success.main"
                          />
                        </TableCell>
                        <TableCell align="right">{it.quantity}</TableCell>
                        <TableCell align="right">
                          {it.unitPrice ?? '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={it.companyProductId ? 'success' : 'warning'}
                            label={
                              it.companyProductId
                                ? `#${it.companyProductId}`
                                : 'Eşleşmedi'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                Olay Günlüğü ({detail.events.length})
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zaman</TableCell>
                    <TableCell>Sıra</TableCell>
                    <TableCell>Statü</TableCell>
                    <TableCell>Sonuç</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.events.map((ev) => (
                    <TableRow key={ev.id}>
                      <TableCell>{fmt(ev.createdAt)}</TableCell>
                      <TableCell>{ev.sequence ?? '—'}</TableCell>
                      <TableCell>
                        {ev.status
                          ? EXTERNAL_ORDER_STATUS_LABELS[ev.status]
                          : (ev.eventType ?? '—')}
                      </TableCell>
                      <TableCell>
                        {ev.outcome ? (
                          <Chip
                            size="small"
                            label={
                              EXTERNAL_EVENT_OUTCOME_LABELS[ev.outcome] ??
                              ev.outcome
                            }
                            color={OUTCOME_COLOR[ev.outcome] ?? 'default'}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {/* Fatura bağlantısı — sağlayıcı bağlantının 10 yıl erişilebilir
                kalmasını şart koşar; bu yükümlülük bağlantı hedefine aittir. */}
            <Box>
              <Typography variant="h5" gutterBottom>
                Fatura Bağlantısı
              </Typography>
              {invoiceError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1 }}
                  onClose={() => setInvoiceError(null)}
                >
                  {invoiceError}
                </Alert>
              )}
              {detail.invoiceSentAt && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {fmt(detail.invoiceSentAt)} tarihinde platforma gönderildi.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fatura adresi (https://…)"
                  value={invoiceInput}
                  disabled={invoiceBusy}
                  onChange={(e) => setInvoiceInput(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={invoiceBusy || invoiceInput.trim().length < 8}
                  onClick={handleSendInvoice}
                >
                  Gönder
                </Button>
                {detail.invoiceSentAt && (
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={invoiceBusy}
                    onClick={handleDeleteInvoice}
                  >
                    Sil
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Gönderilen bağlantının yasal olarak 10 yıl erişilebilir kalması
                gerekir. Hatalı gönderdiyseniz silip yeniden gönderebilirsiniz.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailDialog;
